import { defineStore } from 'pinia'
import LogRocket from 'logrocket'
import userApi from '@/api/user'
import favoritesApi from '@/api/favorites'
import { FavoritableType } from 'common'
import auth0Plugin from '@/setups/authSetup'
import auth0, { type WebAuth } from 'auth0-js'
import * as Sentry from '@sentry/vue'
import type {
  AccessUser,
  ReportVisitHistoryEntry,
  OrganizationAttributes,
  UserAttributes,
  PropertyReportSubscriptionAttributes,
  FeatureFlag,
  UserUpdatePayload,
  FavoriteEntry
} from 'common'
import { usePropertyReportStore } from '@/stores/propertyreport'
import { useStripeStore } from './stripe'
import {
  notStarted,
  loaded,
  error,
  isLoaded,
  isError,
  isNotStarted,
  type Loading
} from '@/utils/loading'

type UserUsageData = { limit: number; numCreated: number } | null

declare const Intercom: (
  fnc: string,
  obj: {
    company?: {
      id: number
      name: string
      size: number
      custom_attributes?: {
        feature_flags: FeatureFlag[]
      }
    }
    user_id?: string
    name?: string
    email?: string
    user_hash?: string
    hide_default_launcher?: boolean
    'Organization Name'?: string
    'Organization ID'?: string | number
    'Has Limited Access'?: boolean
    'Has Property Reports'?: boolean
    Seats?: number
    'Uses SSO'?: boolean
    feature_flags?: string
    'Job Type'?: string
  }
) => void

declare global {
  interface Window {
    pendo?: {
      initialize: (options: Record<string, unknown>) => void
      identify: (options: Record<string, unknown>) => void
      track?: (eventName: string, metadata?: Record<string, unknown>) => void
      isReady?: () => boolean
    }
  }
}

type ReportVisitTypeValue = 'property_report' | 'market_analysis' | 'dataset' | 'rent_roll'
type RowWithId = { id: number | string }

interface RecentReportVisitEntry {
  report_type: ReportVisitTypeValue
  report_id: number
  last_visited_at: string
  report_name?: string | null
}

const REPORT_HISTORY_LOCAL_STORAGE_KEY = 'recentReportVisitsV1'
const REPORT_HISTORY_FALLBACK_LIMIT = 5
const REPORT_HISTORY_LOCAL_STORAGE_MAX = 50

const VALID_REPORT_VISIT_TYPES = new Set<ReportVisitTypeValue>([
  'property_report',
  'market_analysis',
  'dataset',
  'rent_roll'
])

function reportVisitKey(reportType: ReportVisitTypeValue, reportId: number): string {
  return `${reportType}:${reportId}`
}

function favoriteKey(favoritableType: FavoritableType, favoritableId: number): string {
  return `${favoritableType}:${favoritableId}`
}

const favoriteOpChains = new Map<string, Promise<void>>()

async function whenSettled(promise: Promise<unknown>): Promise<void> {
  try {
    await promise
  } catch {
    // A failed op must not block the ops queued behind it.
  }
}

function serializeFavoriteOp<T>(keys: string[], op: () => Promise<T>): Promise<T> {
  const priorOps = keys
    .map((key) => favoriteOpChains.get(key))
    .filter((promise): promise is Promise<void> => promise !== undefined)

  const run = Promise.all(priorOps).then(op)
  const finished = whenSettled(run)

  for (const key of keys) {
    favoriteOpChains.set(key, finished)
  }

  void finished.then(() => {
    for (const key of keys) {
      if (favoriteOpChains.get(key) === finished) {
        favoriteOpChains.delete(key)
      }
    }
  })

  return run
}

function visitTimestamp(value: string): number {
  const timestamp = new Date(value).getTime()
  return Number.isNaN(timestamp) ? 0 : timestamp
}

function parseReportVisitEntry(value: unknown): RecentReportVisitEntry | null {
  if (!value || typeof value !== 'object') {
    return null
  }

  const item = value as Record<string, unknown>
  const reportType = item['report_type']
  const reportId = item['report_id']
  const lastVisitedAt = item['last_visited_at']
  const reportName = item['report_name']

  if (
    typeof reportType !== 'string' ||
    !VALID_REPORT_VISIT_TYPES.has(reportType as ReportVisitTypeValue)
  ) {
    return null
  }

  if (typeof reportId !== 'number' || !Number.isInteger(reportId)) {
    return null
  }

  if (typeof lastVisitedAt !== 'string' || Number.isNaN(new Date(lastVisitedAt).getTime())) {
    return null
  }

  return {
    report_type: reportType as ReportVisitTypeValue,
    report_id: reportId,
    last_visited_at: new Date(lastVisitedAt).toISOString(),
    report_name: typeof reportName === 'string' ? reportName : null
  }
}

function normalizeReportVisits(entries: unknown[]): RecentReportVisitEntry[] {
  const deduped = new Map<string, RecentReportVisitEntry>()

  for (const value of entries) {
    const parsed = parseReportVisitEntry(value)
    if (!parsed) {
      continue
    }

    const key = reportVisitKey(parsed.report_type, parsed.report_id)
    const existing = deduped.get(key)
    if (
      !existing ||
      visitTimestamp(parsed.last_visited_at) > visitTimestamp(existing.last_visited_at)
    ) {
      deduped.set(key, {
        ...parsed,
        report_name: parsed.report_name ?? existing?.report_name ?? null
      })
    } else if (!existing.report_name && parsed.report_name) {
      deduped.set(key, {
        ...existing,
        report_name: parsed.report_name
      })
    }
  }

  return Array.from(deduped.values()).sort(
    (a, b) => visitTimestamp(b.last_visited_at) - visitTimestamp(a.last_visited_at)
  )
}

export const useUserStore = defineStore('user', {
  state: () => ({
    organizationMembers: [] as UserAttributes[],
    filteredOrganizationMembers: null as UserAttributes[] | null,
    organizationMembersTotal: 0 as number,
    allOrganizationMembers: [] as UserAttributes[],
    peopleInOrgOrShared: [] as AccessUser[],
    organizationReportAccesses: null as (UserAttributes & { report_ids: number[] })[] | null,
    organizationReportAccessesTotal: 0 as number,
    organization: null as OrganizationAttributes | null,
    organizationLoading: false,
    reportTagsWithCount: [] as { tag_name: string; count: number }[],
    organizationInvitation: null as OrganizationAttributes | null,
    recentReports: [] as { name: string; id: string }[],
    webAuth: null as WebAuth | null,
    reportHistory: [] as RecentReportVisitEntry[],
    localReportVisits: [] as RecentReportVisitEntry[],
    localReportVisitsLoaded: false,
    reportHistoryInitialized: false,
    reportHistoryInitPromise: null as Promise<void> | null,
    favorites: [] as FavoriteEntry[],
    favoritesInitialized: false,
    favoritesInitPromise: null as Promise<void> | null,
    userUsageResponse: notStarted as Loading<UserUsageData>
  }),
  getters: {
    user: () => auth0Plugin.user.value,
    organizationMembersPerId: (state) => {
      return new Map((state.organizationMembers || []).map((_) => [_.id, _]))
    },
    peopleInOrgOrSharedPerId: (state): Map<string, AccessUser> => {
      return new Map(state.peopleInOrgOrShared.map((_) => [_.id!, _]))
    },
    isHelloDataEmployee: () => {
      const user = auth0Plugin.user.value
      const email = user?.email?.toLowerCase()
      if (!email) {
        return false
      }
      return email.endsWith('@hellodata.ai') || email.endsWith('@gracehill.com')
    },
    isAdmin: (state) => {
      if (state.organization) {
        const ownerIds = [
          state.organization.owner_user_id,
          ...state.organization.admin_user_ids
        ] as (string | undefined)[]
        const user = auth0Plugin.user.value
        return ownerIds.includes(user?.sub)
      }
      return false
    },
    availableReportTags: (state) => {
      const allTags = state.organization?.report_tags || []
      return allTags.sort((a, b) => a.localeCompare(b))
    },
    propertyReportSeats: (): number => {
      return auth0Plugin.user.value?.propertyReportSeats || 0
    },
    helloDataSalesMembers: (): UserAttributes[] => {
      const salespeople = [
        'gmaxwell@hellodata.ai',
        'jbulkowski@hellodata.ai',
        'nbasala@hellodata.ai',
        'marc@hellodata.ai',
        'mpolger@hellodata.ai'
      ]
      return salespeople.flatMap((salesEmail) => {
        const member = (useUserStore().organizationMembers || []).find(
          (_) => _.email === salesEmail
        )
        return member ? member : []
      })
    },
    userUsage: (state): { limit: number; numCreated: number } | null => {
      if (isLoaded(state.userUsageResponse) && state.userUsageResponse.data) {
        return state.userUsageResponse.data
      }
      return null
    },
    userUsageLimitReached: (state): boolean => {
      const usage = state.userUsageResponse
      if (isError(usage) || isNotStarted(usage)) {
        return true
      }
      if (isLoaded(usage)) {
        if (usage.data === null) return false
        return usage.data.numCreated >= usage.data.limit
      }
      return true
    },
    userUsageHelpText: (state): string => {
      if (isError(state.userUsageResponse)) {
        return 'Unable to load your report usage. Please refresh the page or try again later.'
      }
      if (isLoaded(state.userUsageResponse) && state.userUsageResponse.data) {
        return `Your maximum number of reports (${state.userUsageResponse.data.limit}) has been reached. Delete an existing report or contact your organization's administrator to increase your usage limits.`
      }
      return ''
    },
    canCreatePropertyReports: (): boolean => {
      return auth0Plugin.idTokenClaims?.value?.appPermissions?.can_create_property_reports ?? false
    },
    canCreateNewDevelopments: (): boolean => {
      return auth0Plugin.idTokenClaims?.value?.appPermissions?.can_create_new_developments ?? false
    },
    canEditPropertyReports: (): boolean => {
      return auth0Plugin.idTokenClaims?.value?.appPermissions?.can_edit_property_reports ?? false
    },
    canOrgSharePropertyReports: (): boolean => {
      return (
        auth0Plugin.idTokenClaims?.value?.appPermissions?.can_org_share_property_reports ?? false
      )
    },
    canCreateQueryBuilderDatasets: (): boolean => {
      return (
        auth0Plugin.idTokenClaims?.value?.appPermissions?.can_create_query_builder_datasets ?? false
      )
    },
    canCreateMarketAnalytics: (): boolean => {
      return auth0Plugin.idTokenClaims?.value?.appPermissions?.can_create_market_analytics ?? false
    },
    allOrganizationMembersPerId: (state): Map<string, UserAttributes> => {
      return new Map(state.allOrganizationMembers.map((_) => [_.id, _]))
    },
    mergedReportHistory: (state): RecentReportVisitEntry[] => {
      const serverHistory = normalizeReportVisits(state.reportHistory)
      if (serverHistory.length >= REPORT_HISTORY_FALLBACK_LIMIT) {
        return serverHistory
      }

      const merged = [...serverHistory]
      const seen = new Set(serverHistory.map((_) => reportVisitKey(_.report_type, _.report_id)))
      const localHistory = normalizeReportVisits(state.localReportVisits)

      for (const visit of localHistory) {
        if (merged.length >= REPORT_HISTORY_FALLBACK_LIMIT) {
          break
        }

        const key = reportVisitKey(visit.report_type, visit.report_id)
        if (seen.has(key)) {
          continue
        }

        merged.push(visit)
        seen.add(key)
      }

      return merged
    },
    allKnownReportHistory: (state): RecentReportVisitEntry[] => {
      return normalizeReportVisits([
        ...(state.reportHistory || []),
        ...(state.localReportVisits || [])
      ])
    },
    reportVisitRanks(): Map<string, number> {
      return new Map(
        this.mergedReportHistory.map((visit, index) => [
          reportVisitKey(visit.report_type, visit.report_id),
          index
        ])
      )
    },
    favoriteKeys(): Set<string> {
      return new Set(
        this.favorites.map((fav) => favoriteKey(fav.favoritable_type, fav.favoritable_id))
      )
    },
    isFavorite(): (favoritableId: number, favoritableType?: FavoritableType) => boolean {
      const keys = this.favoriteKeys
      return (favoritableId, favoritableType = FavoritableType.PROPERTY_REPORT) =>
        keys.has(favoriteKey(favoritableType, favoritableId))
    }
  },
  actions: {
    allowedToAdmin(recordUserId: string | undefined, allowReportOwner: boolean) {
      const currentUserId = this.user?.sub
      if (allowReportOwner && recordUserId === currentUserId) {
        // If the record owner is the current user, they can admin
        return true
      }
      // If the record owner and the current user are in the same org and the current user is an admin
      const reportOwnerInOrg = (this.organizationMembers || []).find((_) => _.id === recordUserId)
      const currentUserInOrg = (this.organizationMembers || []).find((_) => _.id === currentUserId)
      if (reportOwnerInOrg && currentUserInOrg && this.organization) {
        return (
          this.organization.owner_user_id === currentUserId ||
          this.organization.admin_user_ids.includes(currentUserInOrg.id)
        )
      }
      return false
    },
    initHelpSoftware() {
      const user = auth0Plugin.user.value
      if (import.meta.env.VITE_INTERCOM_WEBSITE_ID && user && Intercom) {
        const hasName = Boolean(user.given_name && user.family_name)
        Intercom('update', {
          company: this.organization?.id
            ? {
                id: this.organization.id,
                name: this.organization.display_name || '',
                size: (this.organizationMembers || []).length,
                custom_attributes: {
                  feature_flags: user.internalFeatureFlags || []
                }
              }
            : undefined,
          user_id: user.sub,
          email: user.email,
          name: hasName ? user.name : undefined,
          user_hash: user.intercomHash,
          hide_default_launcher: true,
          'Uses SSO': this.organization?.connection_id ? true : false,
          'Has Limited Access': useStripeStore().hasLimitedAccess,
          'Has Property Reports': useStripeStore().hasPropertyReports,
          Seats: this.propertyReportSeats,
          feature_flags: (user.internalFeatureFlags || []).join(','),
          'Job Type': user.jobTitle || ''
        })
      }
      if (import.meta.env.MODE === 'production' && import.meta.env.VITE_SENTRY_DSN) {
        Sentry.setUser({
          id: user?.sub,
          email: user?.email
        })
      }
      // disable logrocket when not in production
      if (import.meta.env.VITE_LOG_ROCKET_APP_ID && user && import.meta.env.PROD) {
        try {
          LogRocket.init(import.meta.env.VITE_LOG_ROCKET_APP_ID, {
            serverURL: 'https://app.hellodata.ai/feedback/lr/i',
            network: {
              responseSanitizer: (response) => {
                // if the url is `**/property/<uuid>` ignore the body
                if (response.url && response.url.match(/\/property\/[a-f0-9-]+/)) {
                  return {
                    ...response,
                    body: null
                  }
                }
                return response
              },
              requestSanitizer: (request) => {
                if (
                  request.url.includes('app.hellodata.ai/feedback/') ||
                  request.url.includes('plausible.io/api/event')
                ) {
                  // ignore the request response pair
                  return null
                }

                // otherwise log the request normally
                return request
              }
            }
          })
          LogRocket.identify(user.sub || '', {
            name: user.name || '',
            email: user.email || '',
            organization_id: this.organization?.id || '',
            organization_name: this.organization?.display_name || '',
            has_limited_access: useStripeStore().hasLimitedAccess,
            has_property_reports: useStripeStore().hasPropertyReports,
            seats: this.propertyReportSeats,
            job_title: user.jobTitle ?? null
          })
          console.log('LogRocket initialized successfully')
        } catch (error) {
          console.warn('LogRocket could not be initialized:', error)
        }
      } else {
        console.warn('LogRocket init skipped:', {
          hasAppId: !!import.meta.env.VITE_LOG_ROCKET_APP_ID,
          hasUser: !!user,
          isProd: import.meta.env.PROD
        })
      }
      if (import.meta.env.VITE_PENDO_API_KEY && user && window.pendo) {
        const stripeStore = useStripeStore()
        const pendoPayload = {
          visitor: {
            id: `${user.email} (HD)`,
            visitorId: user.sub,
            email: user.email,
            firstName: user.given_name,
            lastName: user.family_name,
            jobTitle: user.jobTitle ?? null
          },
          account: {
            id: `${this.organization?.display_name} (HD)`,
            accountId: this.organization?.id,
            organizationName: this.organization?.display_name,
            accountCreationDate: stripeStore.subscriptions[0]?.created ?? null,
            hasLimitedAccess: stripeStore.hasLimitedAccess,
            products: [
              stripeStore.hasPropertyReports && 'Property Reports',
              stripeStore.hasMarketReports && 'Market Analytics',
              stripeStore.hasFinancialAnalysis && 'Financial Analysis',
              stripeStore.hasBulkData && 'Bulk Data',
              stripeStore.hasQueryBuilder && 'Query Builder',
              stripeStore.hasRentRollParser && 'Rent Roll Parser',
              stripeStore.hasMarketFinder && 'Market Finder',
              stripeStore.hasRevenueManagement && 'Revenue Management',
              stripeStore.hasDevelopmentPipeline && 'Development Pipeline',
              stripeStore.hasRentProjections && 'Rent Projections',
              stripeStore.hasAPI && 'API'
            ].filter(Boolean)
          }
        }
        if (window.pendo.isReady?.()) {
          window.pendo.identify(pendoPayload)
        } else {
          // Route guide content + event ingest through the first-party proxy
          window.pendo.initialize({
            ...pendoPayload,
            contentHost: 'https://app.hellodata.ai/feedback/p',
            dataHost: 'https://app.hellodata.ai/feedback/p'
          })
        }
      }
    },
    fetchReportTagsWithCount() {
      return userApi.getOrganizationTagsWithCount().then((data) => {
        this.reportTagsWithCount = data ?? []
      })
    },
    initOrganization(showLoading: boolean = true) {
      if (showLoading) {
        this.organizationLoading = true
      }
      return userApi
        .getOrganization()
        .then((data) => {
          this.organization = data
          return data
        })
        .finally(() => {
          this.organizationLoading = false
        })
    },
    resendVerifyEmail() {
      return userApi.resendVerifyEmail()
    },
    updateUserData(payload: UserUpdatePayload) {
      return userApi.updateUserData(payload).then(() => {
        const user = auth0Plugin.user.value
        const member = this.organizationMembers?.find((_) => _.id === user?.sub)
        if (member) {
          member.given_name = payload.given_name ?? null
          member.family_name = payload.family_name ?? null
          member.job_title = payload.user_metadata?.job_title ?? null
        }
      })
    },
    removeFromOrganization(userId: string, transferUserId?: string) {
      return userApi.removeFromOrganization(userId, transferUserId).then(() => {
        this.organizationMembers = this.organizationMembers.filter((_) => _.id !== userId)
        if (this.organization) {
          this.organization.admin_user_ids = this.organization.admin_user_ids.filter(
            (_) => _ !== userId
          )
        }
      })
    },
    updateOrganization(id: number, org: Omit<OrganizationAttributes, 'id'>) {
      return userApi.updateOrganization(id, org).then((result) => {
        if (result) {
          this.organization = result
        }
        return result
      })
    },
    invitePeople(emails: string[]): Promise<void> {
      return userApi.invitePeople(emails).then(() => {
        userApi
          .listOrganizationMembers()
          .then(({ items }) => {
            this.organizationMembers = items
            this.allOrganizationMembers = items
          })
          .catch((err) => {
            console.error('Error updating organization members', err)
          })
      })
    },
    requestPasswordReset(email: string): Promise<void> {
      const auth = this.webAuth

      if (!auth) {
        return Promise.reject(new Error("Can't reset password without an webAuth client"))
      }

      // Create a new Promise
      return new Promise((resolve, reject) => {
        auth.changePassword(
          { email: email, connection: 'Username-Password-Authentication' },
          (err) => {
            if (err) {
              reject(new Error('Password reset failed')) // Reject the Promise with an error
            } else {
              resolve() // Resolve the Promise successfully
            }
          }
        )
      })
    },
    resendInviteEmail(email: string) {
      return userApi.resendInviteEmail(email)
    },
    seatUpdateInvoice(seats: number) {
      return userApi.seatUpdateInvoice(seats)
    },
    async initInvitation() {
      this.organizationInvitation = await userApi.getInvitation()
    },
    acceptInvitation() {
      return userApi.acceptInvitation().then(() => {
        this.organizationInvitation = null
      })
    },
    denyInvitation() {
      return userApi.denyInvitation().then(() => {
        this.organizationInvitation = null
      })
    },
    loadLocalReportVisits() {
      if (this.localReportVisitsLoaded) {
        return
      }

      const stored = localStorage.getItem(REPORT_HISTORY_LOCAL_STORAGE_KEY)
      if (!stored) {
        this.localReportVisits = []
        this.localReportVisitsLoaded = true
        return
      }

      try {
        this.localReportVisits = normalizeReportVisits(JSON.parse(stored)).slice(
          0,
          REPORT_HISTORY_LOCAL_STORAGE_MAX
        )
      } catch (e) {
        console.error('Error parsing report history from localStorage', e, stored)
        this.localReportVisits = []
        localStorage.removeItem(REPORT_HISTORY_LOCAL_STORAGE_KEY)
      }
      this.localReportVisitsLoaded = true
    },
    persistLocalReportVisits() {
      localStorage.setItem(
        REPORT_HISTORY_LOCAL_STORAGE_KEY,
        JSON.stringify(this.localReportVisits.slice(0, REPORT_HISTORY_LOCAL_STORAGE_MAX))
      )
    },
    async initReportHistory() {
      this.loadLocalReportVisits()
      if (this.reportHistoryInitialized) {
        return Promise.resolve()
      }
      if (this.reportHistoryInitPromise) {
        return this.reportHistoryInitPromise
      }

      this.reportHistoryInitPromise = userApi
        .getReportHistory()
        .then((history: ReportVisitHistoryEntry[]) => {
          this.reportHistory = normalizeReportVisits(history)
        })
        .catch((e) => {
          console.error('Error loading report history from API', e)
          this.reportHistory = []
        })
        .finally(() => {
          this.reportHistoryInitialized = true
          this.reportHistoryInitPromise = null
        })

      return this.reportHistoryInitPromise
    },
    async initFavorites() {
      if (this.favoritesInitialized) {
        return Promise.resolve()
      }
      if (this.favoritesInitPromise) {
        return this.favoritesInitPromise
      }

      this.favoritesInitPromise = favoritesApi
        .list()
        .then((favorites) => {
          this.favorites = favorites
        })
        .catch((e) => {
          console.error('Error loading favorites from API', e)
          this.favorites = []
        })
        .finally(() => {
          this.favoritesInitialized = true
          this.favoritesInitPromise = null
        })

      return this.favoritesInitPromise
    },
    async applyAddFavorites(
      favoritableIds: number[],
      favoritableType: FavoritableType
    ): Promise<boolean> {
      const existing = this.favoriteKeys
      const newIds = favoritableIds.filter((id) => !existing.has(favoriteKey(favoritableType, id)))
      if (!newIds.length) {
        return true
      }
      // Optimistically add, then roll back the added ids if the request fails.
      const entries: FavoriteEntry[] = newIds.map((id) => ({
        favoritable_type: favoritableType,
        favoritable_id: id
      }))
      this.favorites = [...entries, ...this.favorites]
      let ok = false
      try {
        ok = await favoritesApi.add({
          favoritable_type: favoritableType,
          favoritable_ids: newIds
        })
      } finally {
        if (!ok) {
          const addedIds = new Set(newIds)
          this.favorites = this.favorites.filter(
            (fav) => !(fav.favoritable_type === favoritableType && addedIds.has(fav.favoritable_id))
          )
        }
      }
      return ok
    },
    async applyRemoveFavorites(
      favoritableIds: number[],
      favoritableType: FavoritableType
    ): Promise<boolean> {
      if (!favoritableIds.length) {
        return true
      }
      const removedIds = new Set(favoritableIds)
      const removedEntries = this.favorites.filter(
        (fav) => fav.favoritable_type === favoritableType && removedIds.has(fav.favoritable_id)
      )
      this.favorites = this.favorites.filter(
        (fav) => !(fav.favoritable_type === favoritableType && removedIds.has(fav.favoritable_id))
      )
      let ok = false
      try {
        ok = await favoritesApi.remove({
          favoritable_type: favoritableType,
          favoritable_ids: favoritableIds
        })
      } finally {
        if (!ok) {
          const present = new Set(
            this.favorites.map((fav) => favoriteKey(fav.favoritable_type, fav.favoritable_id))
          )
          const toRestore = removedEntries.filter(
            (fav) => !present.has(favoriteKey(fav.favoritable_type, fav.favoritable_id))
          )
          if (toRestore.length) {
            this.favorites = [...toRestore, ...this.favorites]
          }
        }
      }
      return ok
    },
    async addFavorites(
      favoritableIds: number[],
      favoritableType: FavoritableType = FavoritableType.PROPERTY_REPORT
    ): Promise<boolean> {
      const keys = favoritableIds.map((id) => favoriteKey(favoritableType, id))
      return serializeFavoriteOp(keys, () =>
        this.applyAddFavorites(favoritableIds, favoritableType)
      )
    },
    async removeFavorites(
      favoritableIds: number[],
      favoritableType: FavoritableType = FavoritableType.PROPERTY_REPORT
    ): Promise<boolean> {
      const keys = favoritableIds.map((id) => favoriteKey(favoritableType, id))
      return serializeFavoriteOp(keys, () =>
        this.applyRemoveFavorites(favoritableIds, favoritableType)
      )
    },
    async toggleFavorite(
      favoritableId: number,
      favoritableType: FavoritableType = FavoritableType.PROPERTY_REPORT
    ): Promise<boolean> {
      const key = favoriteKey(favoritableType, favoritableId)
      return serializeFavoriteOp([key], () => {
        if (this.favoriteKeys.has(key)) {
          return this.applyRemoveFavorites([favoritableId], favoritableType)
        }
        return this.applyAddFavorites([favoritableId], favoritableType)
      })
    },
    recordReportVisitOffline(params: {
      report_type: ReportVisitTypeValue
      report_id: number
      report_name?: string
      last_visited_at?: string | Date
    }) {
      const lastVisitedAt =
        typeof params.last_visited_at === 'string'
          ? params.last_visited_at
          : params.last_visited_at?.toISOString() || new Date().toISOString()

      const entry = parseReportVisitEntry({
        report_type: params.report_type,
        report_id: params.report_id,
        last_visited_at: lastVisitedAt,
        report_name: params.report_name || null
      })
      if (!entry) {
        return
      }

      this.loadLocalReportVisits()
      this.reportHistory = normalizeReportVisits([entry, ...this.reportHistory])
      this.localReportVisits = normalizeReportVisits([entry, ...this.localReportVisits]).slice(
        0,
        REPORT_HISTORY_LOCAL_STORAGE_MAX
      )
      this.persistLocalReportVisits()

      if (params.report_type === 'property_report' && params.report_name) {
        this.addRecentReport({
          id: params.report_id.toString(),
          name: params.report_name
        })
      }
    },
    removeReportVisit(reportType: ReportVisitTypeValue, reportId: number) {
      const key = reportVisitKey(reportType, reportId)
      this.reportHistory = this.reportHistory.filter(
        (_) => reportVisitKey(_.report_type, _.report_id) !== key
      )

      this.loadLocalReportVisits()
      this.localReportVisits = this.localReportVisits.filter(
        (_) => reportVisitKey(_.report_type, _.report_id) !== key
      )
      this.persistLocalReportVisits()

      if (reportType === 'property_report') {
        this.deleteRecentReport(reportId.toString())
      }
    },
    sortRowsByReportVisit<T extends RowWithId>(rows: T[], reportType: ReportVisitTypeValue): T[] {
      const rankMap = this.reportVisitRanks
      return rows
        .map((row, index) => {
          const id = typeof row.id === 'string' ? parseInt(row.id, 10) : row.id
          const key = Number.isNaN(id) ? '' : reportVisitKey(reportType, id)
          const rank = key ? rankMap.get(key) : undefined
          return {
            row,
            index,
            rank: rank ?? Number.MAX_SAFE_INTEGER
          }
        })
        .sort((a, b) => a.rank - b.rank || a.index - b.index)
        .map((_) => _.row)
    },
    getRecentReports(): { name: string; id: string }[] {
      // get recent reports from localStorage ({name, id}[])
      if (this.recentReports.length > 0) {
        return this.recentReports
      }

      const recentReports = localStorage.getItem('recentReports')

      if (recentReports) {
        try {
          return JSON.parse(recentReports)
        } catch (e) {
          console.error('Error parsing recent reports from localStorage', e, recentReports)
          localStorage.removeItem('recentReports')
          return []
        }
      }
      return []
    },
    addRecentReport(report: { name: string; id: string }) {
      // add report to localStorage (max 5)
      const recentReports = this.getRecentReports()
      const newRecentReports = [report, ...recentReports.filter((_) => _.id !== report.id)].slice(
        0,
        5
      )
      if (report.name && report.id) {
        localStorage.setItem('recentReports', JSON.stringify(newRecentReports))
      } else {
        console.error('Invalid report', report)
      }
      this.recentReports = newRecentReports
    },
    deleteRecentReport(reportId: string) {
      // delete report from localStorage
      const recentReports = this.getRecentReports()
      const newRecentReports = recentReports.filter((_) => _.id !== reportId)
      localStorage.setItem('recentReports', JSON.stringify(newRecentReports))
      this.recentReports = newRecentReports
    },
    initWebAuth() {
      this.webAuth = new auth0.WebAuth({
        domain: import.meta.env.VITE_AUTH0_DOMAIN,
        clientID: import.meta.env.VITE_AUTH0_CLIENT_ID
      })
    },
    async initPeopleInOrgOrShared() {
      return userApi.getPeopleInOrgOrShared().then((results) => {
        this.peopleInOrgOrShared = results
      })
    },
    hasFeatureFlag(
      report: PropertyReportSubscriptionAttributes | null,
      flag: FeatureFlag
    ): boolean {
      return (
        (report?.feature_flags || auth0Plugin.user.value?.internalFeatureFlags)?.includes(flag) ??
        false
      )
    },
    reportInOrg(report: { user_id?: string | null } | null) {
      const ownerIds = (this.organizationMembers || []).map((_) => _.id)
      if (report?.user_id && ownerIds.includes(report.user_id)) {
        return true
      }
      return false
    },
    initOrganizationReportAccesses(
      page?: number,
      limit?: number,
      search?: string,
      sortBy?: string,
      descending?: boolean
    ) {
      const pageVal = page || 1
      const limitVal = limit || 10
      const searchVal = search || ''
      return userApi
        .listOrganizationReportAccesses(pageVal, limitVal, searchVal, sortBy, descending)
        .then(({ items, total }) => {
          this.organizationReportAccesses = items
          this.organizationReportAccessesTotal = total
        })
    },
    initOrganizationMembers(
      filters?: {
        search?: string
        sort_by?: string
        descending?: boolean
        group_ids?: (number | null)[]
      },
      pagination?: {
        page?: number
        limit?: number
      }
    ) {
      return userApi.listOrganizationMembers(filters, pagination).then(({ items, total }) => {
        if (!filters && !pagination) {
          this.organizationMembers = items
          this.allOrganizationMembers = items
        } else {
          this.filteredOrganizationMembers = items
          this.organizationMembersTotal = total
        }
      })
    },
    fetchAllOrganizationMembers(filters?: {
      search?: string
      sort_by?: string
      descending?: boolean
      group_ids?: (number | null)[]
    }) {
      return userApi.listOrganizationMembers(filters)
    },
    reportAccessDelete(user: UserAttributes & { report_ids: number[] }) {
      const deletePromises = (user.report_ids || []).map((id) => {
        return usePropertyReportStore().deleteAccess(id, user.id)
      })
      return Promise.all(deletePromises).then(() => {
        this.organizationReportAccesses = (this.organizationReportAccesses || []).filter(
          (_) => _.id !== user.id
        )
      })
    },
    changeOrganizationMemberGroup(groupId: number | null, userIds: string[]) {
      this.organizationMembers = (this.organizationMembers || []).map((_) => {
        if (userIds.includes(_.id)) {
          return { ..._, group_id: groupId }
        }

        return _
      })
      this.allOrganizationMembers = (this.allOrganizationMembers || []).map((_) => {
        if (userIds.includes(_.id)) {
          return { ..._, group_id: groupId }
        }

        return _
      })
    },
    removeOrganizationMembersFromGroup(groupId: number) {
      this.organizationMembers = (this.organizationMembers || []).map((_) => {
        if (_.group_id === groupId) {
          return { ..._, group_id: null }
        }

        return _
      })
      this.allOrganizationMembers = (this.allOrganizationMembers || []).map((_) => {
        if (_.group_id === groupId) {
          return { ..._, group_id: null }
        }

        return _
      })
    },
    bulkAssignSearch(query?: string, types?: string, page?: number, limit?: number) {
      return userApi
        .bulkAssignSearch(
          query ?? undefined,
          types ?? undefined,
          page ?? undefined,
          limit ?? undefined
        )
        .then((data) => {
          return data
        })
    },
    async getUserUsage() {
      try {
        const usage = await userApi.getUserUsage()
        this.userUsageResponse = loaded(usage)
        return this.userUsageResponse
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e))
        this.userUsageResponse = error(err)
        throw err
      }
    },
    refreshPropertyReportLimitUsage() {
      return this.getUserUsage()
    }
  }
})
